import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Paper, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Select, MenuItem, FormControl, InputLabel, IconButton, Typography,
  Stack, Card, CardContent, Box, Divider, Collapse, Chip,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  CircularProgress, Alert, Snackbar,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

const API_BASE = 'https://portal.peaksoftcon.com:8081/api';

const FILE_TITLES = [
  'Proje onay yazısı',
  'İş planı',
  'Proje bitirme belgesi',
];

// "0 9 * * *" → "her gün 09:00". Anything with a day/month/weekday restriction
// is shown verbatim rather than mistranslated. Reading the real cron off the
// bot keeps this text from drifting the way the old hardcoded "02:00" did.
const cronLabel = (expr) => {
  if (!expr) return null;
  const parts = String(expr).trim().split(/\s+/);
  if (parts.length !== 5) return expr;
  const [min, hour, dom, mon, dow] = parts;
  if (dom === '*' && mon === '*' && dow === '*' && /^\d{1,2}$/.test(min) && /^\d{1,2}$/.test(hour)) {
    return `her gün ${hour.padStart(2, '0')}:${min.padStart(2, '0')}`;
  }
  return expr;
};

// The bot names its schedules itself; accept the plausible spellings rather
// than hardcoding one and silently showing nothing if it differs.
const pickCron = (map, ...names) => {
  for (const n of names) if (map && map[n]) return map[n];
  return null;
};

function StatusChip({ label }) {
  if (!label || label === '-') return <span>-</span>;
  const color = /tamamland/i.test(label) ? 'success'
    : /değerlend|düzenleni/i.test(label) ? 'warning'
    : /red|iptal/i.test(label)           ? 'error'
    : /onay/i.test(label)                ? 'success'
    : 'default';
  return <Chip size="small" label={label} color={color} variant="outlined" />;
}

function ProjectsTable({ rows, schedule }) {
  if (!rows) return <CircularProgress size={20} />;
  if (rows.length === 0) return (
    <Alert severity="info" variant="outlined">
      Proje verisi yok{schedule ? ` — bot ${schedule} otomatik çalışır` : ''}.
      {' '}“Şimdi Çalıştır” ile hemen tetikleyebilirsiniz.
    </Alert>
  );
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'action.hover' }}>
            <TableCell>Proje Kodu</TableCell>
            <TableCell>STB Kodu</TableCell>
            <TableCell>Proje Adı</TableCell>
            <TableCell>Kategori</TableCell>
            <TableCell>Onay Durumu</TableCell>
            <TableCell>Süre</TableCell>
            <TableCell>T. Başlangıç</TableCell>
            <TableCell>Tah. Bitiş</TableCell>
            <TableCell>Ek Süre</TableCell>
            <TableCell>Bitiş</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((p) => {
            const r = p.rawData || {};
            return (
              <TableRow key={p.id} hover>
                <TableCell>{r.ProjeKodu || '-'}</TableCell>
                <TableCell>{r.BSTBProjeKodu || '-'}</TableCell>
                <TableCell
                  sx={{ maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  title={r.Adi}
                >
                  {r.Adi || '-'}
                </TableCell>
                <TableCell>{r.ProjeKategorisi || '-'}</TableCell>
                <TableCell><StatusChip label={r.OnayDurumu} /></TableCell>
                <TableCell>{r.ProjeSuresi ? `${r.ProjeSuresi} ay` : '-'}</TableCell>
                <TableCell>{r.TeknoparkBaslangicTarihi || '-'}</TableCell>
                <TableCell>{r.TahminiBitisSuresi || '-'}</TableCell>
                <TableCell>{r.TalepEdilenTahminiBitisSuresi || '-'}</TableCell>
                <TableCell>{r.BitisTarihi || '-'}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function CompletionsTable({ rows, schedule }) {
  if (!rows) return <CircularProgress size={20} />;
  if (rows.length === 0) return (
    <Alert severity="info" variant="outlined">
      Sonlandırma verisi yok{schedule ? ` — bot ${schedule} otomatik çalışır` : ''}.
      {' '}“Şimdi Çalıştır” ile hemen tetikleyebilirsiniz.
    </Alert>
  );
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'action.hover' }}>
            <TableCell>Proje Kodu</TableCell>
            <TableCell>Proje Adı</TableCell>
            <TableCell>Bakanlık Onay Kodu</TableCell>
            <TableCell>Bitirme Talep Durumu</TableCell>
            <TableCell>Bitiş Tarihi</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((p) => {
            const r = p.rawData || {};
            return (
              <TableRow key={p.id} hover>
                <TableCell>{r.ProjeKodu || '-'}</TableCell>
                <TableCell
                  sx={{ maxWidth: 240, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  title={r.Adi || r.ProjeAdi}
                >
                  {r.Adi || r.ProjeAdi || '-'}
                </TableCell>
                <TableCell>{r.BakanlıkOnayKodu || r.BakanlikOnayKodu || '-'}</TableCell>
                <TableCell><StatusChip label={r.BitirmeTalepDurumu} /></TableCell>
                <TableCell>{r.ProjeBitisTarihi || r.BitisTarihi || '-'}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function RunButton({ running, onClick }) {
  return (
    <Button
      size="small"
      variant="outlined"
      disabled={running}
      onClick={onClick}
      startIcon={running ? <CircularProgress size={14} /> : <PlayArrowIcon />}
    >
      {running ? 'Başlatıldı…' : 'Şimdi Çalıştır'}
    </Button>
  );
}

function AdminProjectTracking() {
  const [companies,   setCompanies]   = useState([]);
  const [projects,    setProjects]    = useState({});     // cid → rows | null(loading)
  const [completions, setCompletions] = useState({});
  const [files,       setFiles]       = useState({});
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const [uploadOpen,  setUploadOpen]  = useState(false);
  const [uploadCid,   setUploadCid]   = useState(null);
  const [uploadFile,  setUploadFile]  = useState(null);
  const [uploadTitle, setUploadTitle] = useState(FILE_TITLES[0]);
  const [uploading,   setUploading]   = useState(false);

  const [running,   setRunning]   = useState(() => new Set()); // "<cid>:projects" | "<cid>:completions"
  const [toast,     setToast]     = useState(null);            // { severity, text }
  const [schedules, setSchedules] = useState(null);            // { name: cronExpr }

  const projectsSchedule    = cronLabel(pickCron(schedules, 'projects', 'project'));
  const completionsSchedule = cronLabel(pickCron(schedules, 'completions', 'project-completions'));

  useEffect(() => {
    axios.get(`${API_BASE}/bot/companies`)
      .then((r) => setCompanies(r.data || []))
      .catch((e) => console.error('companies fetch error:', e));

    axios.get(`${API_BASE}/bot/schedules`)
      .then((r) => {
        const list = (r.data && r.data.schedules) || [];
        setSchedules(Object.fromEntries(list.map((s) => [s.name, s.expr])));
      })
      .catch(() => setSchedules({})); // fall back to schedule-free wording
  }, []);

  // `quiet` skips the loading placeholder — used by the post-run refetches so
  // the table doesn't blink back to a spinner every time we poll.
  const fetchProjects = useCallback((cid, quiet = false) => {
    if (!quiet) setProjects((p) => ({ ...p, [cid]: null }));
    axios.get(`${API_BASE}/bot/companies/${cid}/projects`)
      .then((r) => setProjects((p) => ({ ...p, [cid]: r.data || [] })))
      .catch(() => setProjects((p) => ({ ...p, [cid]: [] })));
  }, []);

  const fetchCompletions = useCallback((cid, quiet = false) => {
    if (!quiet) setCompletions((p) => ({ ...p, [cid]: null }));
    axios.get(`${API_BASE}/bot/companies/${cid}/completions`)
      .then((r) => setCompletions((p) => ({ ...p, [cid]: r.data || [] })))
      .catch(() => setCompletions((p) => ({ ...p, [cid]: [] })));
  }, []);

  const fetchFiles = useCallback((cid) => {
    axios.get(`${API_BASE}/projectFiles/${cid}`)
      .then((r) => setFiles((f) => ({ ...f, [cid]: r.data || [] })))
      .catch((e) => console.error('files fetch error:', e));
  }, []);

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        if (!(id in projects))    fetchProjects(id);
        if (!(id in completions)) fetchCompletions(id);
        if (!files[id])           fetchFiles(id);
      }
      return next;
    });
  };

  // Manual trigger for the scrape that otherwise only runs on the nightly cron.
  // The bot answers as soon as it has queued the job — the actual scrape drives
  // a real browser and finishes long after, so we re-poll instead of blocking.
  const triggerRun = async (cid, kind) => {
    const key = `${cid}:${kind}`;
    if (running.has(key)) return;
    setRunning((s) => new Set(s).add(key));

    const label = kind === 'projects' ? 'Proje bilgileri' : 'Sonlandırma listesi';
    try {
      await axios.post(`${API_BASE}/bot/companies/${cid}/run-${kind}`, {});
      setToast({ severity: 'success', text: `${label} güncellemesi başlatıldı — tablo birkaç dakika içinde tazelenir.` });

      const refetch = kind === 'projects' ? fetchProjects : fetchCompletions;
      [10000, 30000, 60000, 120000].forEach((ms) => setTimeout(() => refetch(cid, true), ms));
    } catch (e) {
      setToast({ severity: 'error', text: `${label} çalıştırılamadı: ${e.response?.data?.error || e.message}` });
    } finally {
      // Held briefly so a double-click can't queue the same scrape twice.
      setTimeout(() => {
        setRunning((s) => { const n = new Set(s); n.delete(key); return n; });
      }, 3000);
    }
  };

  const openUpload = (cid) => {
    setUploadCid(cid);
    setUploadFile(null);
    setUploadTitle(FILE_TITLES[0]);
    setUploadOpen(true);
  };

  const submitUpload = async () => {
    if (!uploadFile || !uploadCid) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file',   uploadFile);
    fd.append('baslik', uploadTitle);
    try {
      await axios.post(`${API_BASE}/projectFiles/${uploadCid}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadOpen(false);
      fetchFiles(uploadCid);
    } catch (e) {
      console.error('upload error:', e);
    } finally {
      setUploading(false);
    }
  };

  const deleteFile = (fileId, cid) => {
    if (!window.confirm('Bu dosyayı silmek istediğinize emin misiniz?')) return;
    axios.delete(`${API_BASE}/projectFiles/files/${fileId}`)
      .then(() => fetchFiles(cid))
      .catch((e) => console.error('delete file error:', e));
  };

  const downloadFile = (fileId, fileName) => {
    const a = document.createElement('a');
    a.href = `${API_BASE}/projectFiles/files/${fileId}/download`;
    a.download = fileName || '';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>Proje Takibi</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {projectsSchedule || completionsSchedule ? (
          <>
            Proje bilgileri{projectsSchedule ? ` ${projectsSchedule}` : ''}, sonlandırma listesi
            {completionsSchedule ? ` ${completionsSchedule}` : ''} otomatik güncellenir.
          </>
        ) : (
          'Proje bilgileri ve sonlandırma listesi otomatik güncellenir.'
        )}
        {' '}Beklemek istemezseniz firma kartını açıp “Şimdi Çalıştır” butonunu kullanın.
      </Typography>

      <Stack spacing={1.5}>
        {companies.length === 0 && (
          <Paper sx={{ py: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">Bot'a kayıtlı firma bulunamadı.</Typography>
          </Paper>
        )}

        {companies.map((company) => {
          const expanded    = expandedIds.has(company.id);
          const compProj    = projects[company.id];
          const compCompl   = completions[company.id];
          const compFiles   = files[company.id] || [];

          const projCount  = Array.isArray(compProj)  ? compProj.length  : 0;
          const complCount = Array.isArray(compCompl) ? compCompl.length : 0;

          return (
            <Card
              key={company.id}
              variant="outlined"
              sx={{
                borderLeft: '4px solid',
                borderLeftColor: projCount > 0 ? 'primary.main' : 'divider',
              }}
            >
              {/* ── Başlık ── */}
              <CardContent
                sx={{ cursor: 'pointer', '&:last-child': { pb: 1.5 } }}
                onClick={() => toggleExpand(company.id)}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                    <AccountTreeIcon fontSize="small" color="action" />
                    <Typography fontWeight={600}>{company.name}</Typography>
                    {projCount > 0 && (
                      <Chip size="small" label={`${projCount} proje`} color="primary" variant="outlined" />
                    )}
                    {complCount > 0 && (
                      <Chip size="small" label={`${complCount} sonlandırma`} color="warning" variant="outlined" />
                    )}
                    {compFiles.length > 0 && (
                      <Chip size="small" label={`${compFiles.length} dosya`} color="secondary" variant="outlined" />
                    )}
                  </Stack>
                  <ExpandMoreIcon
                    sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform .2s', flexShrink: 0 }}
                  />
                </Stack>
              </CardContent>

              {/* ── Genişletilmiş içerik ── */}
              <Collapse in={expanded}>
                <Divider />
                <CardContent>

                  {/* ─ Proje Bilgileri ─ */}
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <AccountTreeIcon fontSize="small" color="primary" />
                    <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
                      Proje Bilgileri
                    </Typography>
                    <RunButton
                      running={running.has(`${company.id}:projects`)}
                      onClick={() => triggerRun(company.id, 'projects')}
                    />
                  </Stack>
                  <Box sx={{ mb: 3 }}>
                    <ProjectsTable rows={compProj} schedule={projectsSchedule} />
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  {/* ─ Proje Sonlandırma Listesi ─ */}
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <AssignmentLateIcon fontSize="small" color="warning" />
                    <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
                      Proje Sonlandırma Listesi
                    </Typography>
                    <RunButton
                      running={running.has(`${company.id}:completions`)}
                      onClick={() => triggerRun(company.id, 'completions')}
                    />
                  </Stack>
                  <Box sx={{ mb: 3 }}>
                    <CompletionsTable rows={compCompl} schedule={completionsSchedule} />
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  {/* ─ Dosyalar ─ */}
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600}>Belgeler</Typography>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<CloudUploadIcon />}
                      onClick={() => openUpload(company.id)}
                    >
                      Dosya Yükle
                    </Button>
                  </Stack>

                  {compFiles.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">Henüz belge yüklenmemiş.</Typography>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'action.hover' }}>
                            <TableCell>Belge Türü</TableCell>
                            <TableCell>Dosya Adı</TableCell>
                            <TableCell>Tarih</TableCell>
                            <TableCell align="right">İşlem</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {compFiles.map((f) => (
                            <TableRow key={f.id} hover>
                              <TableCell>
                                <Chip size="small" label={f.baslik} variant="outlined" />
                              </TableCell>
                              <TableCell>{f.file_name}</TableCell>
                              <TableCell>
                                {f.uploaded_at ? new Date(f.uploaded_at).toLocaleDateString('tr-TR') : '-'}
                              </TableCell>
                              <TableCell align="right">
                                <IconButton size="small" onClick={() => downloadFile(f.id, f.file_name)}>
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" color="error" onClick={() => deleteFile(f.id, company.id)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Collapse>
            </Card>
          );
        })}
      </Stack>

      {/* ── Dosya yükleme dialogu ── */}
      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Belge Yükle</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel>Belge Türü</InputLabel>
            <Select
              label="Belge Türü"
              value={uploadTitle}
              onChange={(e) => setUploadTitle(e.target.value)}
            >
              {FILE_TITLES.map((t) => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" component="label" fullWidth>
            {uploadFile ? uploadFile.name : 'Dosya Seç…'}
            <input type="file" hidden onChange={(e) => setUploadFile(e.target.files[0])} />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)} disabled={uploading}>İptal</Button>
          <Button
            variant="contained"
            onClick={submitUpload}
            disabled={!uploadFile || uploading}
            startIcon={uploading ? <CircularProgress size={14} /> : null}
          >
            {uploading ? 'Yükleniyor…' : 'Yükle'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)}>
            {toast.text}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}

export default AdminProjectTracking;
